import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  Typography,
} from "@mui/material";
import { Button } from "pepsico-ds";
import PropTypes from "prop-types";
import { useEffect, useMemo, useRef, useState } from "react";
import "./surveyOutcome.scss";

const HTML_TAG_REGEX = /<[a-z][\s\S]*>/i;

function OptionLabel({ text }) {
  const isHtml = useMemo(() => HTML_TAG_REGEX.test(text), [text]);
  const ref = useRef(null);

  useEffect(() => {
    if (!isHtml || !ref.current) return;
    const images = ref.current.querySelectorAll("img");
    images.forEach((img) => {
      img.onerror = () => {
        img.style.display = "none";
      };
    });
  }, [isHtml, text]);

  if (!isHtml) return <>{text}</>;

  return (
    <span
      ref={ref}
      className="survey-option-html-label"
      dangerouslySetInnerHTML={{ __html: text }}
    />
  );
}

export default function SurveyQuestionSelector({
  survey,
  onConditionAdd,
  onCancel,
  onBack,
  initialSelections,
  initialLogicOperator,
}) {
  const [selections, setSelections] = useState({});

  // Pre-populate selections from initialSelections
  useEffect(() => {
    if (initialSelections?.length && survey?.questions) {
      const initialMap = {};
      initialSelections.forEach((sel) => {
        const matchingQuestion = survey.questions.find(
          (question) => question.questionId === sel.question_id
        );
        if (matchingQuestion) {
          const ids =
            sel.selected_option_ids ||
            (sel.selected_option_id ? [sel.selected_option_id] : []);
          initialMap[matchingQuestion.questionId] = ids;
        }
      });
      setSelections(initialMap);
    }
  }, [initialSelections, survey]);

  const handleAnswerSelect = (questionId, optionId, isChecked) => {
    setSelections((prev) => {
      const current = prev[questionId] || [];
      if (isChecked) {
        return { ...prev, [questionId]: [...current, optionId] };
      }
      return { ...prev, [questionId]: current.filter((id) => id !== optionId) };
    });
  };

  const selectableQuestions = (survey?.questions || []).filter(
    (q) => q.answerOptions?.length > 0
  );

  const atLeastOneAnswered =
    selectableQuestions.length > 0 &&
    selectableQuestions.some((q) => selections[q.questionId]?.length > 0);

  const handleAddCondition = () => {
    const answerOptions = [];
    const conditionSelections = [];

    survey.questions.forEach((question) => {
      const selectedIds = selections[question.questionId] || [];
      if (selectedIds.length > 0) {
        if (selectedIds.length > 1) {
          answerOptions.push({
            logic_operator: "or",
            options: selectedIds,
          });
        } else {
          answerOptions.push(selectedIds[0]);
        }
        const selectedTexts = selectedIds.map((id) => {
          const opt = question.answerOptions.find((o) => o.optionId === id);
          return opt?.optionText || "";
        });
        conditionSelections.push({
          question_id: question.questionId,
          question_title: question.questionTitle,
          selected_option_ids: selectedIds,
          selected_option_texts: selectedTexts,
        });
      }
    });

    const ruleData = {
      logic_operator: initialLogicOperator || "and",
      answer_options: answerOptions,
      condition_display: {
        survey_id: survey.surveyId,
        survey_name: survey.surveyName,
        source_partner: survey.sourcePartner,
        survey: survey,
        selections: conditionSelections,
      },
    };

    onConditionAdd(ruleData);
  };

  return (
    <div className="survey-question-selector" data-testid="survey-question-selector">
      <Typography
        variant="h6"
        style={{ fontSize: "22px", fontWeight: 700, marginBottom: "16px" }}
      >
        Add Condition
      </Typography>
      <Divider style={{ marginBottom: "16px" }} />
      <div
        className="survey-question-selector__header"
        style={{ display: "flex", alignItems: "center", gap: "8px" }}
      >
        <IconButton
          onClick={onBack}
          data-testid="back-button"
          style={{ color: "#1e3a6e" }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" style={{ fontSize: "16px", fontWeight: 700 }}>
          {survey?.surveyName}
        </Typography>
      </div>

      <div className="survey-question-selector__questions">
        {selectableQuestions.map((question) => (
          <div
            key={question.questionId}
            className="survey-question-selector__question"
            data-testid={`question-${question.questionId}`}
          >
            <p className="survey-question-selector__question-title">
              {question.questionTitle}
            </p>
            <FormGroup>
              {question.answerOptions.map((option) => (
                <FormControlLabel
                  key={option.optionId}
                  control={
                    <Checkbox
                      size="small"
                      checked={
                        selections[question.questionId]?.includes(option.optionId) ||
                        false
                      }
                      onChange={(e) =>
                        handleAnswerSelect(
                          question.questionId,
                          option.optionId,
                          e.target.checked
                        )
                      }
                    />
                  }
                  label={<OptionLabel text={option.optionText} />}
                  data-testid={`option-${option.optionId}`}
                />
              ))}
            </FormGroup>
          </div>
        ))}
      </div>

      <Divider style={{ marginBottom: "16px" }} />

      <div className="survey-question-selector__actions">
        <Button variant="tertiary" text="Cancel" size="medium" onClick={onCancel} />
        <Button
          variant="primary"
          text="Add condition"
          size="medium"
          onClick={handleAddCondition}
          disabled={!atLeastOneAnswered}
        />
      </div>
    </div>
  );
}

SurveyQuestionSelector.propTypes = {
  survey: PropTypes.shape({
    surveyId: PropTypes.string,
    surveyName: PropTypes.string,
    sourcePartner: PropTypes.string,
    questions: PropTypes.arrayOf(
      PropTypes.shape({
        questionId: PropTypes.string,
        questionTitle: PropTypes.string,
        answerOptions: PropTypes.arrayOf(
          PropTypes.shape({
            optionId: PropTypes.string,
            optionText: PropTypes.string,
          })
        ),
      })
    ),
  }).isRequired,
  onConditionAdd: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  initialSelections: PropTypes.arrayOf(
    PropTypes.shape({
      question_id: PropTypes.string,
      selected_option_ids: PropTypes.arrayOf(PropTypes.string),
      selected_option_id: PropTypes.string,
    })
  ),
  initialLogicOperator: PropTypes.string,
};
